/**
 * NotificationBell — real-time notification centre.
 *
 * Delivery strategy (in priority order):
 *  1. WebSocket via /api/notifications/ws  ← primary, instant
 *  2. 60-second persisted-state reconciliation while visible  ← recovers missed events
 *
 * Why WebSocket instead of SSE: infrastructure proxies (Replit dev server,
 * nginx, load balancers) kill long-lived HTTP streaming connections. WebSocket
 * upgrades survive every proxy layer — the same path webpack HMR uses.
 *
 * Sound:
 *  - Synthesised via Web Audio API — no audio file, no CDN dependency.
 *  - Plays only for genuinely new notifications (IDs not seen in the initial
 *    load or in a prior WebSocket/poll cycle).
 *  - Skipped when the tab is hidden (document.hidden) or when the AudioContext
 *    hasn't been unlocked by a user gesture yet (browser autoplay policy).
 *  - Duplicate guard: each notification ID is tracked in a Set; the same ID
 *    will never trigger a second sound regardless of reconnects or poll cycles.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, BellOff, Check, ExternalLink } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { API } from "@/api";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { fmtDateTime } from "@/utils/fmt";

// ── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS   = 60_000;  // fallback polling cadence
const SSE_MAX_FAILURES   = 3;       // consecutive errors before showing polling status
const SSE_BACKOFF_MS     = [1_000, 3_000, 8_000]; // retry delays per failure index

// ── Style maps ───────────────────────────────────────────────────────────────

const PRIORITY_DOT = {
  low:    "#94a3b8",
  normal: "#3b82f6",
  high:   "#f59e0b",
  urgent: "#f43f5e",
};

const MODULE_COLOR = {
  Sales:      "#2563eb",
  Production: "#d97706",
  QC:         "#059669",
  Purchase:   "#7c3aed",
  Accounts:   "#0891b2",
  Dispatch:   "#4338ca",
  Inventory:  "#e11d48",
  System:     "#475569",
  General:    "#64748b",
};

// ── Web Audio chime ──────────────────────────────────────────────────────────

// ── Web Audio — robust autoplay-safe chime ───────────────────────────────────
//
// Chrome / Mobile Chrome rules:
//   • AudioContext created outside a user-gesture starts in "suspended" state.
//   • ctx.resume() only succeeds when called *inside* a gesture handler.
//   • Calling ctx.resume() is async — ctx.state is still "suspended" until
//     the returned Promise resolves.
//
// Strategy:
//   1. Create the AudioContext lazily, but only *resume* it inside a gesture.
//   2. Listen for click, touchstart, keydown, pointerdown so any interaction
//      unlocks audio — tap on Android fires touchstart before click.
//   3. If playChime() is called while locked, queue one pending chime.
//      It plays the moment the user next touches the page.
//   4. _doPlayChime() is the raw synthesiser — never called unless running.

let _audioCtx     = null;   // singleton AudioContext
let _audioReady   = false;  // true once ctx.state === "running"
let _pendingChime = false;  // at most one queued chime waiting for unlock

function _getCtx() {
  if (!_audioCtx) {
    try {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (_) {
      return null;
    }
  }
  return _audioCtx;
}

function _doPlayChime() {
  const ctx = _audioCtx;
  if (!ctx || ctx.state !== "running") return;

  const now   = ctx.currentTime;
  const notes = [880, 1320];   // root → fifth

  notes.forEach((freq, i) => {
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type            = "sine";
    osc.frequency.value = freq;

    const t = now + i * 0.08;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.15, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

    osc.start(t);
    osc.stop(t + 0.5);
  });
}

// Called from gesture handlers — creates ctx if needed, awaits resume, flushes queue.
function unlockAudio() {
  const ctx = _getCtx();
  if (!ctx) return;

  if (ctx.state === "running") {
    _audioReady = true;
    if (_pendingChime && !document.hidden) {
      _pendingChime = false;
      _doPlayChime();
    }
    return;
  }

  // state === "suspended" | "closed" — try to resume inside this gesture frame
  ctx.resume().then(() => {
    _audioReady = ctx.state === "running";
    if (_audioReady && _pendingChime && !document.hidden) {
      _pendingChime = false;
      _doPlayChime();
    }
  }).catch(() => {});
}

// Register all meaningful gesture events — belt-and-suspenders for Android Chrome.
// Each handler auto-removes itself after firing once per event type.
["click", "touchstart", "keydown", "pointerdown"].forEach((evt) => {
  document.addEventListener(evt, unlockAudio, { once: true, passive: true });
});

/**
 * Request a chime sound.
 * • Plays immediately if the AudioContext is running.
 * • Queues one chime (max) to play on the next user gesture if still locked.
 * • No-ops silently if the tab is hidden or the platform has no Web Audio.
 */
function playChime() {
  if (document.hidden) return;
  if (_audioReady && _audioCtx?.state === "running") {
    _doPlayChime();
  } else {
    // Audio still locked — queue for the next gesture
    _pendingChime = true;
  }
}

// ── Helper ───────────────────────────────────────────────────────────────────

// ── Component ────────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const [open,    setOpen]    = useState(false);
  const [items,   setItems]   = useState([]);
  const [unread,  setUnread]  = useState(0);
  const [loading, setLoading] = useState(false);
  const [sseOn,   setSseOn]   = useState(true);  // visual indicator

  const panelRef  = useRef(null);
  const bellRef   = useRef(null);
  const seenIds   = useRef(new Set());   // IDs fetched / received — guards sound + dedup
  const isInitial = useRef(true);        // true during the very first load
  const nav       = useNavigate();
  const location  = useLocation();

  const {
    state:       pushState,
    loading:     pushLoading,
    error:       pushError,
    subscribe:   subscribePush,
    unsubscribe: unsubscribePush,
    retry:       retryPush,
  } = usePushSubscription();

  // ── Initial load ─────────────────────────────────────────────────────────

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get("/notifications", { params: { limit: 50 } });
      const incoming = res.data.items || [];
      const newOnes  = incoming.filter(it => !seenIds.current.has(it.id));

      // On the very first load mark everything as seen WITHOUT playing sound —
      // these are pre-existing notifications, not new arrivals.
      if (isInitial.current) {
        incoming.forEach(it => seenIds.current.add(it.id));
        isInitial.current = false;
      } else if (newOnes.length > 0) {
        // Subsequent polls: only new IDs get a sound
        newOnes.forEach(it => seenIds.current.add(it.id));
        if (!document.hidden) playChime();
      }

      setItems(incoming);
      setUnread(res.data.unread || 0);
    } catch (_) {
      // notifications are non-critical — fail silently
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Summary clicks only open the panel. Never call markRead/markAllRead here.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("notifications") !== "open") return;
    setOpen(true);
    load({ silent: true });
    // Keep the intent until the user closes the panel, including PWA reloads.
  }, [location.pathname, location.search, location.hash, load]);

  const closePanel = useCallback(() => {
    setOpen(false);
    const params = new URLSearchParams(location.search);
    if (params.get("notifications") !== "open") return;
    params.delete("notifications");
    nav({ pathname: location.pathname, search: params.toString(), hash: location.hash },
      { replace: true });
  }, [location.pathname, location.search, location.hash, nav]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = event => {
      if (event.data?.type !== "open-notifications") return;
      setOpen(true);
      load({ silent: true });
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [load]);

  // ── WebSocket connection ──────────────────────────────────────────────────

  useEffect(() => {
    // Initial data load (before WebSocket connects — avoids a blank panel on open)
    if (!document.hidden) load();

    let ws           = null;
    let failures     = 0;
    let reconcileId  = null;
    let retryTimeout = null;
    let destroyed    = false;

    function stopReconciliation() {
      if (reconcileId) clearInterval(reconcileId);
      reconcileId = null;
    }

    function scheduleReconciliation() {
      stopReconciliation();
      if (destroyed || document.hidden) return;
      // Keep one persisted-state safety check for cross-worker events. The
      // socket remains the immediate path; this timer is not a second fallback.
      reconcileId = setInterval(() => {
        if (!document.hidden) load({ silent: true });
      }, POLL_INTERVAL_MS);
    }

    function connect() {
      if (destroyed) return;

      // Build ws:// / wss:// URL on the same host — the webpack-dev-server
      // proxy (ws: true) forwards /api/notifications/ws to the backend.
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const wsUrl = `${proto}://${window.location.host}/api/notifications/ws`;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        failures = 0;
        setSseOn(true);
        // Rehydrate anything committed while the connection was unavailable.
        if (!document.hidden) load({ silent: true });
        scheduleReconciliation();
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          // Keepalive ping — ignore
          if (!msg?.id) return;

          // Deduplicate — same ID may arrive on reconnect
          if (seenIds.current.has(msg.id)) return;
          seenIds.current.add(msg.id);

          // Play sound only when tab is visible and audio is unlocked
          if (!document.hidden) playChime();

          // Prepend to the list and bump unread count
          setItems(prev => {
            const already = prev.find(it => it.id === msg.id);
            return already ? prev : [msg, ...prev].slice(0, 200);
          });
          setUnread(n => n + 1);
        } catch (_) {}
      };

      // onerror always fires before onclose — let onclose handle retry logic
      ws.onerror = () => {};

      ws.onclose = () => {
        if (destroyed) return;
        failures += 1;

        if (failures >= SSE_MAX_FAILURES) {
          setSseOn(false);
        }

        // Exponential-ish back-off before reconnecting
        const delay = SSE_BACKOFF_MS[Math.min(failures - 1, SSE_BACKOFF_MS.length - 1)];
        retryTimeout = setTimeout(connect, delay);
      };
    }

    connect();
    scheduleReconciliation();
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopReconciliation();
        return;
      }
      // Reconcile immediately so events missed while hidden are visible before
      // the next periodic check.
      load({ silent: true });
      scheduleReconciliation();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      destroyed = true;
      ws?.close();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stopReconciliation();
      if (retryTimeout)  clearTimeout(retryTimeout);
    };
  }, [load]);

  // ── Click-outside to close panel ─────────────────────────────────────────

  useEffect(() => {
    const h = (e) => {
      if (
        panelRef.current && bellRef.current &&
        !panelRef.current.contains(e.target) &&
        !bellRef.current.contains(e.target)
      ) closePanel();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [closePanel]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(prev => prev.map(it => it.id === id ? { ...it, read: true } : it));
      setUnread(n => Math.max(0, n - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems(prev => prev.map(it => ({ ...it, read: true })));
      setUnread(0);
    } catch (_) {}
  };


  const openDoc = (it) => {
    if (!it.read) markRead(it.id);
    closePanel();
    if (it.link) nav(it.link);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ position: "relative" }}>
      {/* Bell button ─────────────────────────────────────────────────────── */}
      <button
        ref={bellRef}
        onClick={() => open ? closePanel() : setOpen(true)}
        aria-label="Notifications"
        data-testid="topbar-notifications"
        style={{
          width: 36, height: 36,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--flg-bg-elevated)",
          border: "1px solid var(--flg-border)",
          borderRadius: 8, cursor: "pointer",
          color: "var(--flg-fg-muted)",
          position: "relative",
        }}
      >
        <Bell size={16} />

        {/* Unread count badge */}
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -2, right: -2,
            minWidth: 18, height: 18, padding: "0 5px",
            borderRadius: 9,
            background: "var(--flg-coral-500, #f43f5e)",
            color: "white", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid var(--flg-bg-elevated)",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}

        {/* Live indicator — green dot when SSE is connected */}
        <span style={{
          position: "absolute", bottom: 1, right: 1,
          width: 6, height: 6, borderRadius: 3,
          background: sseOn ? "#22c55e" : "#94a3b8",
          border: "1.5px solid var(--flg-bg-elevated)",
          transition: "background 0.4s",
        }} title={sseOn ? "Live" : "Polling"} />
      </button>

      {/* Dropdown panel ──────────────────────────────────────────────────── */}
      {open && (
        <div
          ref={panelRef}
          style={{
            position: "absolute", top: 44, right: 0,
            width: 380, maxWidth: "92vw", maxHeight: "70vh",
            background: "var(--flg-bg-elevated, white)",
            border: "1px solid var(--flg-border)",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
            zIndex: 1000,
            display: "flex", flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 14px",
            borderBottom: "1px solid var(--flg-border)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>Notifications</span>
              <span style={{
                fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
                color: sseOn ? "#16a34a" : "#94a3b8",
                background: sseOn ? "#dcfce7" : "#f1f5f9",
                padding: "1px 6px", borderRadius: 10,
              }}>
                {sseOn ? "● LIVE" : "○ POLLING"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>

            {/* Push notification toggle — only visible when push is available */}
            {pushState === "unsubscribed" && (
              <button
                onClick={subscribePush}
                disabled={pushLoading}
                title="Enable push notifications (receive alerts even when the tab is closed)"
                style={{
                  fontSize: 11, fontWeight: 600,
                  color: "var(--flg-fg-muted)",
                  background: "transparent",
                  border: "1px solid var(--flg-border)",
                  borderRadius: 6, padding: "2px 7px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 3,
                  opacity: pushLoading ? 0.5 : 1,
                }}
              >
                {pushLoading ? "…" : <><Bell size={10} /> Push</>}
              </button>
            )}
            {pushState === "error" && (
              <button
                onClick={retryPush}
                title={pushError || "Push subscription failed — tap to retry"}
                style={{
                  fontSize: 11, fontWeight: 600,
                  color: "#dc2626", background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: 6, padding: "2px 7px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 3,
                }}
              >
                <Bell size={10} /> Retry Push
              </button>
            )}
            {pushState === "subscribed" && (
              <button
                onClick={unsubscribePush}
                disabled={pushLoading}
                title="Push notifications on — click to disable"
                style={{
                  fontSize: 10, fontWeight: 600,
                  color: "#16a34a", background: "#dcfce7",
                  border: "none", borderRadius: 10,
                  padding: "1px 6px", cursor: "pointer",
                  opacity: pushLoading ? 0.5 : 1,
                }}
              >
                {pushLoading ? "…" : "● Push"}
              </button>
            )}
            {pushState === "denied" && (
              <span
                title="Notifications blocked — allow them in browser settings to enable push"
                style={{
                  fontSize: 10, fontWeight: 600,
                  color: "#94a3b8", background: "#f1f5f9",
                  borderRadius: 10, padding: "1px 6px",
                  display: "flex", alignItems: "center", gap: 3,
                }}
              >
                <BellOff size={9} /> Push
              </span>
            )}
            {/* iOS in browser tab: PushManager is absent — prompt to install PWA */}
            {pushState === "unsupported" && /iphone|ipad|ipod/i.test(navigator.userAgent) && (
              <span
                title="iOS push notifications require the app to be installed. In Safari: Share → Add to Home Screen, then open from there."
                style={{
                  fontSize: 10, fontWeight: 600,
                  color: "#92400e", background: "#fef3c7",
                  borderRadius: 10, padding: "1px 6px",
                  display: "flex", alignItems: "center", gap: 3,
                  cursor: "help",
                }}
              >
                <Bell size={9} /> Add to Home Screen
              </span>
            )}

            {unread > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  fontSize: 12, color: "var(--flg-brand-600)",
                  background: "transparent", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 4,
                }}
              >
                <Check size={12} /> Mark all read
              </button>
            )}
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && items.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--flg-fg-muted)", fontSize: 13 }}>
                Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div style={{ padding: 28, textAlign: "center", color: "var(--flg-fg-muted)", fontSize: 13 }}>
                No notifications yet.
              </div>
            )}

            {items.map(it => (
              <NotificationRow
                key={it.id}
                item={it}
                onMarkRead={markRead}
                onOpenDoc={openDoc}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── NotificationRow — extracted to avoid re-rendering the whole list ─────────

function NotificationRow({ item: it, onMarkRead, onOpenDoc }) {
  return (
    <div style={{
      padding: "12px 14px",
      borderBottom: "1px solid var(--flg-border)",
      background: it.read ? "transparent" : "rgba(59,130,246,0.04)",
      display: "flex", gap: 10, alignItems: "flex-start",
    }}>
      {/* Priority dot */}
      <span style={{
        width: 8, height: 8, borderRadius: 4,
        marginTop: 6, flexShrink: 0,
        background: PRIORITY_DOT[it.priority] || PRIORITY_DOT.normal,
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Module label + unread indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{
            fontSize: 10, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.5,
            color: MODULE_COLOR[it.module] || MODULE_COLOR.General,
          }}>
            {it.module}
          </span>
          {!it.read && (
            <span style={{ width: 6, height: 6, borderRadius: 3, background: "#3b82f6" }} />
          )}
        </div>

        {/* Message */}
        <div style={{ fontSize: 13, color: "var(--flg-fg)", lineHeight: 1.4 }}>
          {it.message}
        </div>

        {/* Meta line */}
        <div style={{
          fontSize: 11, color: "var(--flg-fg-muted)",
          marginTop: 4, display: "flex", alignItems: "center", gap: 8,
        }}>
          {it.doc_no && <span>#{it.doc_no}</span>}
          {it.customer_name && <span>· {it.customer_name}</span>}
          <span>· {fmtDateTime(it.created_at)}</span>
          {it.reminder_active === true && <span>· Still pending</span>}
          {it.reminder_active === false && it.reminder_resolved_at && <span>· Resolved</span>}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {it.link && (
            <button
              onClick={() => onOpenDoc(it)}
              style={{
                fontSize: 12, display: "flex", alignItems: "center", gap: 4,
                color: "var(--flg-brand-600)",
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
              }}
            >
              <ExternalLink size={12} /> Open document
            </button>
          )}
          {!it.read && (
            <button
              onClick={() => onMarkRead(it.id)}
              style={{
                fontSize: 12, display: "flex", alignItems: "center", gap: 4,
                color: "var(--flg-fg-muted)",
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
              }}
            >
              <Check size={12} /> Mark read
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
